import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Zap } from 'lucide-react';
import {
  SCHEME_DEADLINE,
  daysUntilDeadline,
} from '@/routes/guides.ev-subsidy-countdown';

// UI-027: PM E-DRIVE Subsidy Countdown Banner
// Replaces the prior expired "extended to 2028" notice with a live countdown
// pointing readers to the full /guides/ev-subsidy-countdown calculator.

const DISMISS_KEY = 'mw_ui027_dismissed';

export function formatBannerLabel(daysLeft: number): string {
  if (daysLeft <= 0) return 'PM E-DRIVE subsidy has ended';
  if (daysLeft === 1) return 'PM E-DRIVE subsidy expires tomorrow';
  return `PM E-DRIVE subsidy expires July 31 — ${daysLeft} days left`;
}

export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  );
  const [daysLeft, setDaysLeft] = useState(() => daysUntilDeadline());

  useEffect(() => {
    const id = setInterval(() => setDaysLeft(daysUntilDeadline()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (dismissed || Date.now() >= SCHEME_DEADLINE.getTime()) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="relative flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border-b border-amber-500/20 px-4 py-2 text-sm">
      <Zap className="h-4 w-4 text-amber-400 flex-shrink-0" />
      <span className="text-amber-200">
        <strong>Heads up:</strong> {formatBannerLabel(daysLeft)}.
      </span>
      <Link
        to="/guides/ev-subsidy-countdown"
        className="font-semibold text-amber-400 hover:text-amber-300 underline underline-offset-2 whitespace-nowrap"
      >
        Calculate your subsidy →
      </Link>
      <button
        onClick={handleDismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
